import React from 'react';
import '../styles/PaymentMarks.css';

/**
 * Trust-building payment marks shown under purchase CTAs.
 * Each mark uses its real brand colours on a white pill so it reads on any background.
 *
 * Props:
 *  - variant: 'light' (default, navy-friendly) | 'dark' (for cream backgrounds)
 *  - align: 'left' | 'center' (default)
 *  - showStripeNote: bool — shows "Secured by Stripe" lockup
 */
const PaymentMarks = ({ variant = 'light', align = 'center', showStripeNote = true }) => {
  return (
    <div className={`pm-wrap pm-${variant} pm-align-${align}`} data-testid="payment-marks">
      <span className="pm-eyebrow">One-tap checkout on mobile</span>
      <div className="pm-row">
        {/* Apple Pay — official-style: apple glyph + "Pay" wordmark, both in near-black */}
        <span className="pm-mark pm-mark-applepay" aria-label="Apple Pay" data-testid="pm-applepay">
          <svg viewBox="0 0 50 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            {/* Apple logo */}
            <path fill="#000" d="M10.3 4.8c.5-.6.8-1.5.7-2.3-.7 0-1.6.4-2.1 1-.5.5-.9 1.4-.8 2.2.8.1 1.6-.4 2.2-.9zm.7 1.1c-1.2-.1-2.2.7-2.7.7-.6 0-1.4-.7-2.4-.7-1.2 0-2.4.7-3 1.8-1.3 2.3-.3 5.7.9 7.6.6.9 1.3 1.9 2.3 1.9.9 0 1.3-.6 2.4-.6s1.4.6 2.4.6 1.6-.9 2.2-1.9c.4-.6.7-1.3 1-2-2.5-1-2.6-4.5.1-5.6-.7-1.1-1.8-1.8-2.7-1.8z"/>
            {/* "Pay" wordmark */}
            <text x="17" y="14" fontFamily="-apple-system, 'Helvetica Neue', Helvetica, Arial, sans-serif" fontSize="11" fontWeight="600" fill="#000">Pay</text>
          </svg>
        </span>

        {/* Google Pay — colored G mark + "Pay" wordmark in black */}
        <span className="pm-mark pm-mark-gpay" aria-label="Google Pay" data-testid="pm-gpay">
          <svg viewBox="0 0 56 20" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            {/* G — multi-color */}
            <path fill="#4285F4" d="M16.6 10.2c0-.7-.1-1.3-.2-2H10v3.8h3.7c-.2 1-.6 1.8-1.4 2.3v1.9h2.2c1.3-1.2 2.1-3 2.1-6z"/>
            <path fill="#34A853" d="M10 17c1.9 0 3.5-.6 4.7-1.7l-2.2-1.9c-.6.4-1.4.7-2.4.7-1.9 0-3.5-1.3-4-3H3.7v1.9C5 15.6 7.3 17 10 17z"/>
            <path fill="#FBBC04" d="M6 11.1c-.1-.4-.2-.8-.2-1.3s.1-.9.2-1.3V6.5H3.7C3.2 7.5 3 8.6 3 9.8s.3 2.3.7 3.3L6 11.1z"/>
            <path fill="#EA4335" d="M10 5.5c1.1 0 2 .4 2.7 1l2-2C13.5 3.4 12 2.8 10 2.8c-2.7 0-5 1.4-6.3 3.7L6 8.4c.5-1.6 2.1-2.9 4-2.9z"/>
            {/* Pay wordmark */}
            <text x="20" y="14" fontFamily="-apple-system, 'Helvetica Neue', Helvetica, Arial, sans-serif" fontSize="11" fontWeight="600" fill="#5F6368">Pay</text>
          </svg>
        </span>

        <span className="pm-sep" aria-hidden="true">·</span>

        {/* Visa — flat blue + tiny gold underline accent */}
        <span className="pm-mark pm-mark-card" aria-label="Visa" data-testid="pm-visa">
          <svg viewBox="0 0 50 16" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <text x="25" y="12" textAnchor="middle" fontFamily="Arial Black, Helvetica, sans-serif" fontSize="13" fontWeight="900" fill="#1A1F71" letterSpacing="0.5">VISA</text>
            <rect x="11" y="13.4" width="28" height="1" fill="#F7B600"/>
          </svg>
        </span>

        {/* Mastercard — overlapping red + orange circles */}
        <span className="pm-mark pm-mark-card" aria-label="Mastercard" data-testid="pm-mc">
          <svg viewBox="0 0 36 22" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <circle cx="14" cy="11" r="7.5" fill="#EB001B"/>
            <circle cx="22" cy="11" r="7.5" fill="#F79E1B"/>
            <path d="M18 5.6a7.5 7.5 0 010 10.8 7.5 7.5 0 010-10.8z" fill="#FF5F00"/>
          </svg>
        </span>

        {/* Amex — blue rounded rect with white wordmark */}
        <span className="pm-mark pm-mark-card pm-mark-amex" aria-label="American Express" data-testid="pm-amex">
          <svg viewBox="0 0 40 16" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <rect width="40" height="16" rx="2.5" fill="#1F72CD"/>
            <text x="20" y="11" textAnchor="middle" fontFamily="Arial Black, Helvetica, sans-serif" fontSize="6.5" fontWeight="900" fill="#fff" letterSpacing="0.6">AMEX</text>
          </svg>
        </span>
      </div>
      {showStripeNote && (
        <div className="pm-stripe">
          <svg className="pm-stripe-lock" viewBox="0 0 12 14" aria-hidden="true">
            <path d="M6 0a3.5 3.5 0 00-3.5 3.5V5H2a1 1 0 00-1 1v7a1 1 0 001 1h8a1 1 0 001-1V6a1 1 0 00-1-1H9.5V3.5A3.5 3.5 0 006 0zm0 1.5a2 2 0 012 2V5H4V3.5a2 2 0 012-2z" fill="currentColor"/>
          </svg>
          <span>Secured by <strong>Stripe</strong> · One-time payment · No subscription</span>
        </div>
      )}
    </div>
  );
};

export default PaymentMarks;
